// Implements: F-UIX-019, F-IDN-027
/**
 * The organizations of the platform (v3 `vOrgs`): name, status badge, people (`member_count`),
 * the month's spend ("—" when the totals are not available) and creation day, with a row menu:
 * Renombrar, Invitar admin (an active organization only: a suspended one grants its people nothing,
 * so an invitee could not enter), and Suspender (danger) or Reactivar by its status.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Avatar } from "../../../shared/ui/atoms/Avatar";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { dayText } from "../../../shared/ui/dayText";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { OrganizationView } from "../../../types/organization";
import styles from "./Organization.module.css";

export interface OrganizationsTableProps {
  readonly organizations: readonly OrganizationView[];
  /** The month's spend of an organization as text, or `null` when unknown. */
  readonly spendOf: (organizationId: string) => string | null;
  readonly onRename: (organization: OrganizationView) => void;
  readonly onInviteAdmin: (organization: OrganizationView) => void;
  readonly onStatus: (organization: OrganizationView) => void;
}

export function OrganizationsTable({
  organizations,
  spendOf,
  onRename,
  onInviteAdmin,
  onStatus,
}: OrganizationsTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">{t("organization.platform.column.name")}</th>
            <th scope="col">{t("organization.platform.column.status")}</th>
            <th scope="col" className={styles.number}>
              {t("organization.platform.column.people")}
            </th>
            <th scope="col" className={styles.number}>
              {t("organization.platform.column.spend")}
            </th>
            <th scope="col">{t("organization.platform.column.created")}</th>
            <th scope="col">
              <span className={styles.srOnly}>{t("organization.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {organizations.map((organization) => {
            const active = organization.status === "active";
            return (
              <tr key={organization.id}>
                <td>
                  <span className={styles.person}>
                    <Avatar name={organization.name} shape="square" />
                    <span className={styles.strong}>{organization.name}</span>
                  </span>
                </td>
                <td>
                  <Badge tone={active ? "success" : "warning"}>
                    {active ? t("organization.platform.status.active") : t("organization.platform.status.suspended")}
                  </Badge>
                </td>
                <td className={styles.number}>{organization.member_count}</td>
                <td className={styles.number}>{spendOf(organization.id) ?? t("common.value.unknown")}</td>
                <td className={styles.meta}>{dayText(organization.created_at)}</td>
                <td>
                  <div className={styles.actions}>
                    <Menu
                      label={t("organization.platform.menuFor", { name: organization.name })}
                      align="end"
                      entries={[
                        {
                          kind: "item",
                          id: "rename",
                          label: t("organization.platform.rename"),
                          onSelect: () => {
                            onRename(organization);
                          },
                        },
                        ...(active
                          ? [
                              {
                                kind: "item" as const,
                                id: "invite-admin",
                                label: t("organization.platform.inviteAdmin"),
                                icon: "user-plus" as const,
                                onSelect: () => {
                                  onInviteAdmin(organization);
                                },
                              },
                            ]
                          : []),
                        active
                          ? {
                              kind: "item",
                              id: "status",
                              label: t("organization.platform.suspend"),
                              icon: "ban",
                              danger: true,
                              onSelect: () => {
                                onStatus(organization);
                              },
                            }
                          : {
                              kind: "item",
                              id: "status",
                              label: t("organization.platform.reactivate"),
                              icon: "play",
                              onSelect: () => {
                                onStatus(organization);
                              },
                            },
                      ]}
                      renderTrigger={(trigger) => (
                        <button
                          type="button"
                          {...trigger}
                          className={buttonClassName("ghost", "md", true)}
                          aria-label={t("organization.platform.menuFor", { name: organization.name })}
                        >
                          <Icon name="more" />
                        </button>
                      )}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
